import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDob, displayName } from "@/lib/patientFormat";
import { ErrLine } from "./AvailabilityScreen";
import { LINK } from "./psMasters";
import { ModifyBtn, SweepBar, Strip, Faint, IBASE, qList, slowErr } from "./intakeShared";

const cap1 = (x) => String(x).charAt(0).toUpperCase() + String(x).slice(1);
const hasChart = (p) => !!(p.chart_uuid || p.has_medical_data);
const drLast = (name) => { const w = displayName(String(name || "").split(",")[0]).trim().split(/\s+/); return w[w.length - 1] || ""; };

function useRoster(cid) {
  return useQuery({ queryKey: ["intake-roster", cid], enabled: !!cid, staleTime: 60_000,
    queryFn: async () => (await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: String(cid) } })).data?.results ?? [] });
}

function FormCard({ qs, isNew, provider }) {
  const [ans, setAns] = useState({});
  const shown = qs.filter((q) => isNew || !q.new_patient_only);
  const tap = (q, o) => setAns((a) => {
    const cur = a[q.id] || [];
    if (q.type === "multi_select") return { ...a, [q.id]: cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o] };
    return { ...a, [q.id]: [o] };
  });
  return (
    <div className="max-w-[520px] border border-[#E9E9EC] rounded-[16px] bg-white" style={{ padding: 18 }} data-testid="in-pv-form">
      {shown.map((q, i) => {
        const opts = q.type === "yes_no" ? (q.options?.length ? q.options : ["Yes", "No"]) : q.options || [];
        return (
          <div key={q.id || i} className="py-3 border-t border-[#E9E9EC] first:border-t-0 first:pt-0" data-testid={`in-pv-q-${i + 1}`}>
            <div className="text-[14px] font-semibold text-[var(--ink)] mb-2">{i + 1}. {q.question}</div>
            {q.type === "text" ? <input placeholder="Type your answer" className="w-full rounded-[12px] border border-[#E9E9EC] px-3.5 py-2 text-[13.5px] outline-none focus:border-[#5856D6]" /> : (
              <div className="flex flex-wrap gap-2">{opts.map((o) => { const on = (ans[q.id || i] || []).includes(o); return (
                <button key={o} type="button" aria-pressed={on} onClick={() => tap({ ...q, id: q.id || i }, o)} className="text-[13.5px] rounded-[12px] border transition-colors"
                  style={{ minHeight: 40, padding: "9px 14px", background: on ? "#141412" : "#fff", color: on ? "#fff" : "var(--ink)", borderColor: on ? "#141412" : "#E9E9EC" }}>{o}</button>); })}</div>
            )}
          </div>
        );
      })}
      <button type="button" disabled className="mt-3 w-full text-[13.5px] font-bold py-2.5 rounded-full text-white" style={{ background: "#141412", opacity: 0.9 }} data-testid="in-pv-send">Send to Dr. {drLast(provider.provider_name)}</button>
    </div>
  );
}

export function PreviewTile({ cid, provider, draft, dirty, hasAny, onGoQuestions, onUseVersion }) {
  const { data: roster = [] } = useRoster(cid);
  const [pid, setPid] = useState("new");
  const [refine, setRefine] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const [res, setRes] = useState(null);
  if (!hasAny) return <Faint testid="in-pv-nothing">No question set to preview yet. <button type="button" className={`${LINK} text-[13px]`} onClick={onGoQuestions} data-testid="in-pv-generate-one">Generate one</button></Faint>;
  const run = async () => {
    const p = roster.find((x) => (x.mrn || x.chart_id) === pid);
    // Doc §6 shape: chart_uuid or has_medical_data ⇒ returning; neither (and no prior note) ⇒ new.
    const patient = p ? { full_name: p.full_name, date_of_birth: p.dob, gender: p.gender || null, mrn: p.mrn || null, chart_uuid: p.chart_uuid || null, has_medical_data: !!p.has_medical_data }
      : { full_name: null, date_of_birth: null, gender: null, mrn: null, chart_uuid: null, has_medical_data: false };
    const r = refine.trim();
    // A second refine builds on the last refined version for the same patient, so refining can be repeated.
    const base = r && res?.refine && res.pid === pid && res.qs.length ? res.qs : dirty ? draft : null;
    setBusy(true); setErr(false);
    try {
      const { data } = await api.post(`${IBASE}/intake-design/preview`, { client_id: String(cid), npi: String(provider.npi_number), patient, ...(r ? { refine: r } : {}), ...(base ? { questions: base } : {}) }, { timeout: 180000 });
      const isNew = data?.is_new_patient ?? !p, pt = data?.patient || {};
      setRes({ qs: qList(data), isNew, name: p ? displayName(p.full_name) : "A new patient", refine: r, pid, run: Date.now(), intro: data?.intro || "", age: pt.age, gender: pt.gender, specialty: data?.taxonomy?.taxonomy_desc || "" });
    } catch (e) { setErr(slowErr(e) ? "slow" : true); } finally { setBusy(false); }
  };
  return (
    <>
      <Strip testid="in-pv-strip">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="flex flex-col gap-1 min-w-[260px]"><span className="text-[12px] text-[var(--gray2)]">Patient</span>
            <select value={pid} onChange={(e) => setPid(e.target.value)} className="rounded-[10px] border border-[#E9E9EC] bg-white px-2.5 py-2 text-[13px] text-[var(--ink)] outline-none focus:border-[#5856D6]" data-testid="in-pv-patient">
              <option value="new">A new patient</option>
              {roster.map((p) => <option key={p.mrn || p.chart_id} value={p.mrn || p.chart_id}>{displayName(p.full_name)} · DOB {fmtDob(p.dob)}{hasChart(p) ? "" : " · no chart yet"}</option>)}
            </select></label>
          <div className="flex-1 min-w-[260px] flex gap-2 items-end">
            <input value={refine} onChange={(e) => setRefine(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !busy) run(); }} placeholder="Shorter, add a sleep question…" data-testid="in-pv-refine"
              className="flex-1 rounded-[10px] border border-[#E9E9EC] px-3 py-2 text-[13px] outline-none focus:border-[#5856D6]" />
            <ModifyBtn busy={busy} busyLabel="Previewing…" onClick={run} testid="in-pv-run">Preview</ModifyBtn>
          </div>
        </div>
        {dirty ? <div className="text-[12.5px] text-[var(--gray3)]" data-testid="in-pv-draft-note">Previewing your unsaved draft.</div> : null}
        {err ? <ErrLine testid="in-pv-error">{err === "slow" ? "The preview took too long this time. Try again." : "Couldn't build the preview. Try again."}</ErrLine> : null}
        {busy ? <div className="text-[12.5px] text-[var(--gray2)]" data-testid="in-pv-busy-note">Building the form… this usually takes 20–40 seconds.</div> : null}
        {busy ? <SweepBar testid="in-pv-sweep" /> : null}
      </Strip>
      {!res ? <Faint testid="in-pv-before">Pick a patient and Preview to see the form exactly as they get it.</Faint> : (
        <div className="mt-[10px]">
          <div className="text-[13px] font-semibold text-[var(--gray2)]" data-testid="in-pv-header">{res.name} · {res.isNew ? "New patient — sees history questions" : "Existing patient — history questions skipped"}</div>
          <div className="text-[12px] text-[var(--gray3)] mt-0.5 mb-[10px]" data-testid="in-pv-meta">{[res.age != null ? `${res.age} y` : null, res.gender && String(res.gender).toLowerCase() !== "unknown" ? cap1(res.gender) : null, res.specialty ? `Built for ${res.specialty}` : null, `${res.qs.length} questions`].filter(Boolean).join(" · ")}</div>
          {!res.qs.length ? <Faint testid="in-pv-empty">No questions came back — try again or change the refine text.</Faint> : <>
            {res.intro ? <div className="max-w-[520px] text-[14px] text-[var(--ink)] mb-2.5 px-1" data-testid="in-pv-intro">{res.intro}</div> : null}
            <FormCard key={res.run} qs={res.qs} isNew={res.isNew} provider={provider} />
          </>}
          {res.refine && res.qs.length ? (
            <div className="max-w-[520px] flex items-center justify-between gap-3 mt-3" data-testid="in-pv-refined">
              <span className="text-[13px] text-[var(--gray2)]">Refined with "{res.refine}" · {res.qs.length} questions</span>
              <button type="button" onClick={() => onUseVersion(res.qs)} className="text-[12.5px] font-semibold px-4 py-2 rounded-full border border-black/[.12] text-[var(--ink2)] flex-none" data-testid="in-pv-use">Use this version</button>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
